import { useState, useCallback, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Save, FolderOpen, Download, Trash2, Loader2, RefreshCw, QrCode, Copy } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";

const PUBLIC_BASE = process.env.REACT_APP_BACKEND_URL;

export default function GuardarInforme({ data, vendedor, periodo, exportOpts }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [qrItem, setQrItem] = useState(null);
  const hasVendor = vendedor && vendedor !== "__all__";

  const publicUrl = (it) => `${PUBLIC_BASE}/api/public/informe/${it.share_token}`;
  const venceTxt = (it) => (it.share_expira ? `vence ${new Date(it.share_expira).toLocaleDateString("es-PY")}` : "");
  const nuevoEnlace = async (it) => {
    try {
      const r = await api.post(`/informes-guardados/${it.id}/nuevo-enlace`);
      setList((l) => l.map((x) => (x.id === it.id ? { ...x, ...r.data } : x)));
      if (qrItem?.id === it.id) setQrItem((q) => ({ ...q, ...r.data }));
      toast.success("Enlace anterior revocado. Nuevo enlace válido por 7 días.");
    } catch { toast.error("No se pudo generar el enlace."); }
  };

  const copyLink = async (it) => {
    try {
      await navigator.clipboard.writeText(publicUrl(it));
      toast.success("Enlace copiado al portapapeles.");
    } catch { toast.error("No se pudo copiar el enlace."); }
  };

  const downloadQR = (it) => {
    const canvas = document.querySelector('[data-testid="qr-canvas"] canvas');
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `QR_${(it.vendedor || "informe").replace(/[^a-z0-9]+/gi, "_")}_${it.periodo}.png`;
    a.click();
  };

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/informes", { params: { periodo } });
      setList(r.data || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [periodo]);

  useEffect(() => { if (open) loadList(); }, [open, loadList]);

  const save = async () => {
    if (!hasVendor) {
      toast.error("Selecciona un vendedor específico para guardar su informe.");
      return;
    }
    setSaving(true);
    try {
      const r = await api.get("/informe-cartera.pdf", { params: { mes: periodo, vendedor, _t: Date.now() }, responseType: "blob" });
      const fd = new FormData();
      fd.append("vendedor", vendedor);
      fd.append("periodo", periodo);
      fd.append("file", r.data, "informe.pdf");
      await api.post("/informes/guardar", fd);
      toast.success(`Informe de ${vendedor} guardado (${periodo}).`);
      loadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar el informe.");
    } finally { setSaving(false); }
  };

  const download = async (it) => {
    try {
      const r = await api.get(`/informes/${it.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = it.filename || "informe.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo descargar el informe."); }
  };

  const remove = async (it) => {
    try {
      await api.delete(`/informes/${it.id}`);
      toast.success("Informe eliminado.");
      loadList();
    } catch { toast.error("No se pudo eliminar el informe."); }
  };

  return (
    <div className="flex items-center gap-2">
      <Button
        data-testid="guardar-informe-button"
        onClick={save}
        disabled={saving}
        variant="outline"
        className="h-9 border-emerald-200 text-emerald-800 hover:bg-emerald-50"
        title={hasVendor ? "Guardar el informe de este vendedor" : "Selecciona un vendedor para guardar su informe"}
      >
        {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
        {hasVendor ? "Guardar informe" : "Elegí un vendedor para guardar"}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button data-testid="informes-guardados-trigger" variant="ghost" className="h-9 text-slate-600 hover:text-emerald-800">
            <FolderOpen className="h-4 w-4 mr-1.5" /> Guardados
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-2xl" data-testid="informes-guardados-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-emerald-700" /> Informes guardados · {periodo}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">
            Cada informe se guarda por vendedor. Para <strong>modificar</strong> uno, cambia los datos y vuelve a pulsar
            «Guardar informe» con ese vendedor seleccionado (se sobrescribe), o elimínalo aquí.
          </p>
          <div className="mt-3 max-h-[420px] overflow-y-auto rounded-lg border border-slate-100">
            {loading ? (
              <div className="flex items-center justify-center py-10 text-slate-400">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Cargando…
              </div>
            ) : list.length === 0 ? (
              <div className="py-10 text-center text-sm text-slate-400" data-testid="informes-empty">
                Aún no hay informes guardados para este mes.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                    <th className="text-left px-4 py-2.5 font-semibold">Vendedor</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Actualizado</th>
                    <th className="text-right px-4 py-2.5 font-semibold">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((it) => (
                    <tr key={it.id} className="border-t border-slate-100" data-testid={`informe-row-${it.id}`}>
                      <td className="px-4 py-2.5 font-medium text-slate-800">{it.vendedor}</td>
                      <td className="px-4 py-2.5 text-slate-500 text-xs">
                        {it.updated_at ? new Date(it.updated_at).toLocaleString("es-ES") : "—"}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center justify-end gap-1.5">
                          <Button size="sm" variant="ghost" className="h-8 text-slate-700" onClick={() => setQrItem(it)} data-testid={`informe-qr-${it.id}`} title="Código QR para descargar">
                            <QrCode className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 text-emerald-700" onClick={() => download(it)} data-testid={`informe-download-${it.id}`}>
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 text-red-600" onClick={() => remove(it)} data-testid={`informe-delete-${it.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="flex justify-end pt-2">
            <Button variant="ghost" className="h-8 text-slate-500" onClick={loadList} data-testid="informes-refresh">
              <RefreshCw className="h-4 w-4 mr-1.5" /> Actualizar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!qrItem} onOpenChange={(v) => !v && setQrItem(null)}>
        <DialogContent className="max-w-sm" data-testid="informe-qr-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5 text-emerald-700" /> QR de descarga
            </DialogTitle>
          </DialogHeader>
          {qrItem && (
            <div className="flex flex-col items-center gap-3">
              <p className="text-sm text-slate-600 text-center">
                {qrItem.vendedor} · {qrItem.periodo}
              </p>
              <div data-testid="qr-canvas" className="rounded-xl border border-slate-200 p-4 bg-white">
                <QRCodeCanvas value={publicUrl(qrItem)} size={220} level="M" includeMargin />
              </div>
              <p className="text-[11px] text-slate-400 text-center break-all px-2">{publicUrl(qrItem)}</p>
              <p className="text-xs text-slate-500 text-center">
                Escanea o comparte este QR: descarga directamente el informe del vendedor, sin iniciar sesión. El enlace {venceTxt(qrItem) || "vence a los 7 días"}.
                <button type="button" className="ml-1 underline text-emerald-700" onClick={() => nuevoEnlace(qrItem)} data-testid="qr-nuevo-enlace">Revocar y generar uno nuevo</button>
              </p>
              <div className="flex gap-2 w-full">
                <Button variant="outline" className="flex-1" onClick={() => copyLink(qrItem)} data-testid="qr-copy-link">
                  <Copy className="h-4 w-4 mr-1.5" /> Copiar enlace
                </Button>
                <Button className="flex-1 bg-emerald-700 hover:bg-emerald-800" onClick={() => downloadQR(qrItem)} data-testid="qr-download-png">
                  <Download className="h-4 w-4 mr-1.5" /> Descargar QR
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
